import React, { useEffect, useState } from "react";
import SlideOutSidebar from "../../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Table from "ui-components/table/table";
import { createColumnHelper } from "@tanstack/react-table";
import { useCustomerViewPickupSummaryDetail } from "hooks/usePickupSummary";
import { CustomerViewPickupSummaryFilterListModel } from "../../../../../app/data/pickup/models";
import { PickupStatus } from "app/data/common/route";
import { Routes } from "app/route/RoutesConfig";
import { useHistory } from "react-router-dom";

interface CustomerViewPickupDetailsProps {
    requesterCompanyId: string;
    requesterCompany?: string;
    isOpen: boolean;
    onClose: () => void;
    filterData: CustomerViewPickupSummaryFilterListModel;
    data?: any[];
    initialStatus?: string;
    isLoading?: boolean;
}

const columnHelper = createColumnHelper<any>();

const CustomerViewPickupDetails: React.FC<CustomerViewPickupDetailsProps> = ({
    requesterCompanyId,
    requesterCompany,
    isOpen,
    onClose,
    filterData,
    data,
    initialStatus,
    isLoading,
}) => {

    const history = useHistory();

    const tabs = ["Created", "Completed", "Missed", "Rescheduled", "Canceled"];
    const [selectedTab, setSelectedTab] = useState(initialStatus || "Created");

    const statusMap: Record<string, PickupStatus> = {
        Created: PickupStatus.CREATED,
        Completed: PickupStatus.COMPLETED,
        Missed: PickupStatus.MISSED,
        Rescheduled: PickupStatus.RESCHEDULE_PICKUP,
        Canceled: PickupStatus.CANCELED,
    };

    const mergedFilter = {
        ...filterData,
        status: statusMap[selectedTab],
        originTerminalNumbers: Array.isArray(filterData.requesterCompanyIds)
            ? filterData.requesterCompanyIds.map((t) => t.value)
            : [],
        ...(filterData.timePeriod === "CUSTOM"
            ? { from: filterData.from, to: filterData.to }
            : { from: undefined, to: undefined }),
    };


    const {
        data: terminalViewDetail,
        isLoading: listLoading,
        refetch: refetchTerminalDetail,
    } = useCustomerViewPickupSummaryDetail(requesterCompanyId, mergedFilter, {
        enabled: false,
    });


    const tableData =
        data && Array.isArray(data) && data.length > 0
            ? data
            : terminalViewDetail?.content && Array.isArray(terminalViewDetail.content)
                ? terminalViewDetail.content
                : [];

    const columns = [
        columnHelper.accessor("formattedPickupNumber", {
            id: "formattedPickupNumber",
            header: "Pickup #",
            cell: (cellProps) => (
                <span
                    className="xgs-table-link-cell"
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(`${Routes.pickup.log}/${cellProps.getValue()}`)
                    }}>
                    {cellProps.getValue()}
                </span >
            ),
            size: 40,
        }),
        columnHelper.accessor("lastEventDate", {
            id: "lastEventDate",
            header: "Last Event Date",
            cell: (cellProps) => <>{cellProps.getValue().toUiDateTimeFormat()}</>,
            size: 80,
        }),
        columnHelper.accessor("originTerminal", {
            id: "originTerminal",
            header: "Origin Terminal",
            size: 100,
        }),
        columnHelper.accessor("shipperName", {
            id: "shipperName",
            header: "Shipper",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.shipperCity,
                        cellProps?.row?.original?.shipperState
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 80,
        }),
        columnHelper.accessor("consigneeName", {
            id: "consigneeName",
            header: "Consignee",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.consigneePostalCode,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 40,
        }),
        columnHelper.accessor("totalUnits", {
            id: "totalUnits",
            header: "Freight Details",
            cell: (cellProps) => (
                <span>
                    Units: {cellProps.getValue()}, lbs: {cellProps?.row?.original?.totalWeight}
                </span>
            ),
            size: 50,
        }),
        columnHelper.accessor("statusNotes", {
            id: "statusNotes",
            header: "Status Notes",
            size: 50,
        }),
    ];

    useEffect(() => {
        if (isOpen && initialStatus) {
            setSelectedTab(initialStatus);
        }
    }, [initialStatus, isOpen]);

    useEffect(() => {
        if (isOpen && requesterCompanyId) {
            refetchTerminalDetail();
        }
    }, [isOpen, requesterCompanyId, selectedTab, refetchTerminalDetail]);

    return (
        <SlideOutSidebar
            header={`${requesterCompany ?? ""} - Pickups`}
            onClose={onClose}
            show={isOpen}
            size="large"
        >
            <div className="tab-wrapper">
                <div className="tab-container">
                    {tabs.map((tab, index) => (
                        <div
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            className={`tab-item ${selectedTab === tab ? 'active' : ''} ${index === 0 ? 'first' : index === tabs.length - 1 ? 'last' : ''
                                }`}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
                <div>
                    {selectedTab === "Created" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Completed" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Missed" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Rescheduled" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : selectedTab === "Canceled" ? (
                        <Table
                            isLoading={isLoading ?? listLoading}
                            columns={columns}
                            data={tableData}
                            rowHeight={58}
                            minTableHeight={220}
                            noResultsText={`No Pickup Summary ${selectedTab} List`}
                            infiniteScroll
                            responsive
                        />
                    ) : null}
                </div>
            </div>
        </SlideOutSidebar>
    );
};

export default CustomerViewPickupDetails;